// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Template download component for the item data upload process
 */
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import {
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@/constants/hardfin-standard-icons';
import {
  UOM_DISPLAY_MAP,
  UOM_OPTIONS_BY_CATEGORY,
} from '@/constants/unit-of-measure';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';

interface TemplateDownloadProps {
  onClose: () => void;
  onNext: () => void;
}

const CORE_COLUMNS = [
  'name',
  'sku',
  'type (device, service, bulk)',
  'description',
];
const BULK_COLUMNS = ['unit of measure'];
// Accounting columns are ordered: useful life, then accounting currency, then
// the cost amounts, with salvage value last -- so the currency precedes every
// decimal amount in the template.
const USEFUL_LIFE_COLUMN = 'useful life (months)';
const SIMPLE_ACCOUNTING_COLUMNS = ['cost basis', 'salvage value'];
const ADVANCED_ACCOUNTING_COLUMNS = [
  'material cost',
  'direct labor',
  'allocated indirect',
  'freight inbound',
  'freight outbound',
  'installation',
  'tax',
  'tariffs',
  'interest',
  'salvage value',
];

const UNIT_OF_MEASURE_GROUPS = UOM_OPTIONS_BY_CATEGORY.map((category) => ({
  label: category.label,
  options: category.options.map((code) => UOM_DISPLAY_MAP[code]),
}));

const ACCOUNTING_FIELD_TYPES: Record<string, string> = {
  'salvage value': 'decimal',
  'useful life (months)': 'integer',
  'cost basis': 'decimal',
  'material cost': 'decimal',
  'direct labor': 'decimal',
  'allocated indirect': 'decimal',
  'freight inbound': 'decimal',
  'freight outbound': 'decimal',
  installation: 'decimal',
  tax: 'decimal',
  tariffs: 'decimal',
  interest: 'decimal',
};

/**
 * Renders the template download step of the item data upload process
 */
export function TemplateDownload(props: TemplateDownloadProps) {
  const { user } = useCurrentUser();
  const orgDefaultCurrency = user.organization.defaultCurrency;
  // The importer strips the "(default ...)" hint; the canonical column is "accounting currency".
  const accountingCurrencyColumn = `accounting currency (default ${orgDefaultCurrency})`;

  const [includeAccounting, setIncludeAccounting] = useState(false);
  const [useAdvancedAccounting, setUseAdvancedAccounting] = useState(false);
  const [uomExpanded, setUomExpanded] = useState(false);

  const getColumns = useCallback(() => {
    const columns = [...CORE_COLUMNS, ...BULK_COLUMNS];

    if (includeAccounting) {
      columns.push(USEFUL_LIFE_COLUMN, accountingCurrencyColumn);
      if (useAdvancedAccounting) {
        columns.push(...ADVANCED_ACCOUNTING_COLUMNS);
      } else {
        columns.push(...SIMPLE_ACCOUNTING_COLUMNS);
      }
    }

    return columns;
  }, [includeAccounting, useAdvancedAccounting, accountingCurrencyColumn]);

  const handleDownloadTemplate = useCallback(() => {
    const columns = getColumns();
    // Quote fields that contain commas for proper CSV formatting
    const escapedColumns = columns.map((col) =>
      col.includes(',') ? `"${col}"` : col
    );
    const csvContent = escapedColumns.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const accountingLabel = !includeAccounting
      ? ''
      : useAdvancedAccounting
        ? '_advanced'
        : '_basic';
    link.setAttribute(
      'download',
      `item_upload_template${accountingLabel}_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [includeAccounting, useAdvancedAccounting, getColumns]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
            maxHeight: '470px',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">
              Template for creating new items
            </Typography>
            <Stack spacing={0} alignItems="flex-end">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeAccounting}
                    onChange={(e) => {
                      setIncludeAccounting(e.target.checked);
                      if (!e.target.checked) {
                        setUseAdvancedAccounting(false);
                      }
                    }}
                  />
                }
                label="Include accounting fields"
                labelPlacement="start"
              />
              {includeAccounting && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={useAdvancedAccounting}
                      onChange={(e) =>
                        setUseAdvancedAccounting(e.target.checked)
                      }
                    />
                  }
                  label="Use advanced costs"
                  labelPlacement="start"
                />
              )}
            </Stack>
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Required fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 12 }}>
            <li>
              <Typography variant="body2">Name - text (required)</Typography>
            </li>
            <li>
              <Typography variant="body2">SKU - text (required)</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Type - device, service, or bulk (required)
              </Typography>
            </li>
          </ul>
          <Typography variant="subtitle2" gutterBottom>
            Optional fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 12 }}>
            <li>
              <Typography variant="body2">
                Description - text (optional)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Unit of measure - for bulk items, default: EA (optional)
              </Typography>
            </li>
          </ul>
          {includeAccounting && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Accounting fields (device items only)
              </Typography>
              <ul
                style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}
              >
                <li key={USEFUL_LIFE_COLUMN}>
                  <Typography variant="body2">
                    {USEFUL_LIFE_COLUMN.charAt(0).toUpperCase() +
                      USEFUL_LIFE_COLUMN.slice(1)}{' '}
                    - {ACCOUNTING_FIELD_TYPES[USEFUL_LIFE_COLUMN]}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Accounting currency -{' '}
                    <Tooltip
                      title={
                        <>
                          Must be a valid ISO 4217 currency code
                          <br />
                          Examples:
                          <br />
                          USD - United States dollar
                          <br />
                          CAD - Canadian dollar
                          <br />
                          EUR - euro
                          <br />
                          GBP - British pound
                          <br />
                          <Link
                            href="https://en.wikipedia.org/wiki/ISO_4217"
                            target="_blank"
                            rel="noopener"
                            color="inherit"
                            underline="always"
                          >
                            See the full list of codes on Wikipedia
                          </Link>
                        </>
                      }
                      arrow
                    >
                      <span
                        style={{
                          textDecorationLine: 'underline',
                          textDecorationStyle: 'dotted',
                        }}
                      >
                        ISO code
                      </span>
                    </Tooltip>{' '}
                    (required when a row includes a monetary amount, otherwise
                    defaults to {orgDefaultCurrency})
                  </Typography>
                </li>
                {(useAdvancedAccounting
                  ? ADVANCED_ACCOUNTING_COLUMNS
                  : SIMPLE_ACCOUNTING_COLUMNS
                ).map((column) => (
                  <li key={column}>
                    <Typography variant="body2">
                      {column.charAt(0).toUpperCase() + column.slice(1)} -{' '}
                      {ACCOUNTING_FIELD_TYPES[column]}
                    </Typography>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mt: 2,
            }}
            onClick={() => setUomExpanded((prev) => !prev)}
          >
            <Typography variant="subtitle2">
              Valid unit of measure options (for bulk items only)
            </Typography>
            {uomExpanded ? (
              <ExpandLessIcon fontSize="small" sx={{ ml: 0.5 }} />
            ) : (
              <ExpandMoreIcon fontSize="small" sx={{ ml: 0.5 }} />
            )}
          </Box>
          <Collapse in={uomExpanded}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                mt: 1.5,
              }}
            >
              {UNIT_OF_MEASURE_GROUPS.map((group) => (
                <Box
                  key={group.label}
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                    p: 1.5,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {group.label}
                  </Typography>
                  {group.options.map((option) => (
                    <Typography key={option} variant="body2">
                      {option}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              Fields <strong>name</strong>, <strong>sku</strong>, and{' '}
              <strong>type</strong> are required for all items
            </li>
            <li>
              The <strong>type</strong> must be one of: device, service, or bulk
            </li>
            <li>
              A <strong>unit of measure</strong> is required for bulk items
              (defaults to EA if not provided)
            </li>
            <li>
              Accounting fields (salvage value, useful life, cost basis, etc.)
              are only valid for device items
            </li>
            <li>
              You cannot provide both <strong>cost basis</strong> and advanced
              cost breakdown fields in the same row
            </li>
            <li>Names and SKUs must be unique within your organization</li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={props.onClose}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={props.onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
